import { Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import { FormattedMessage } from 'react-intl';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import type { SettlementType } from '@/store/option/state.ts';

type SettlementFieldsProps = {
  tileId: string;
};

export function SettlementFields({ tileId }: SettlementFieldsProps) {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [checkOption] = useCheckOption();

  const {
    possibleCutOffMarketPlaces,
    cutOffMarketPlace,
    settlementType,
    possibleSettlementType,
    cashSettlementCurrency,
    possibleCashSettlementCurrencies,
    currencyPair,
  } = useOptionSelector((option) => {
    return {
      cutOffMarketPlace: option.properties.cutOffMarketPlace ?? option.properties.possibleCutOffMarketPlaces?.default,
      possibleCutOffMarketPlaces: option.properties.possibleCutOffMarketPlaces?.possible ?? [],
      possibleSettlementType: option.properties.possibleSettlementTypes?.possible ?? [],
      settlementType: option.properties.settlementType ?? option.properties.possibleSettlementTypes?.default,
      cashSettlementCurrency:
        option.properties.cashSettlementCurrency ?? option.properties.possibleCashSettlementCurrencies?.default,
      possibleCashSettlementCurrencies: option.properties.possibleCashSettlementCurrencies?.possible ?? [],
      rfs: option.properties.rfs,
      currencyPair: option.properties.currencyPair,
    };
  });

  const currencyPairData = useAppSelector((state) => selectCurrencyPairData(state, currencyPair));

  function handleSettlementChange(settlementType: SettlementType) {
    sgwtWebAnalytics?.trackEvent('Option', 'Settlement type changed', settlementType);

    checkOption({
      tileId,
      properties: {
        settlementType,
      },
    });
  }

  function handleMarketPlaceChange(cutOffMarketPlace: string) {
    sgwtWebAnalytics?.trackEvent('Option', 'MarketPlace change', cutOffMarketPlace);

    checkOption({
      tileId,
      properties: {
        cutOffMarketPlace,
      },
    });
  }

  return (
    <OptionFormGroup label={<FormattedMessage id="option.fields.settlement" />}>
      <div className="d-flex flex-grow-1 gap-1">
        <Select
          data-e2e="settlement-type-select"
          className="form-select form-select-sm"
          selectedItemValue={settlementType}
          itemsAsObjects={possibleSettlementType}
          onChange={(settlementType) => handleSettlementChange(settlementType)}
        />
        {!currencyPairData?.notDeliverable && (
          <Select
            data-e2e="cash-settlement-currency-select"
            className="form-select form-select-sm"
            selectedItemValue={cashSettlementCurrency}
            onChange={(cashSettlementCurrency) =>
              (() => {
                sgwtWebAnalytics?.trackEvent('Option', 'Cash settlement currency changed', cashSettlementCurrency);
                return checkOption({
                  tileId,
                  properties: {
                    cashSettlementCurrency,
                  },
                });
              })()
            }
            itemsAsObjects={possibleCashSettlementCurrencies}
          />
        )}
        <Select
          data-e2e="cut-off-marketplace-select"
          className="form-select form-select-sm"
          selectedItemValue={cutOffMarketPlace}
          onChange={(marketPlace) => handleMarketPlaceChange(marketPlace)}
          itemsAsObjects={possibleCutOffMarketPlaces}
        />
      </div>
    </OptionFormGroup>
  );
}
